import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Upload, Sliders, CreditCard, PackageCheck,
  ChevronDown, ArrowRight, PlayCircle, MessageCircleQuestion,
} from "lucide-react";
import { useBusinessInfo } from "@/contexts/BusinessInfoContext";
import { toYouTubeEmbed } from "@/lib/youtube";

/**
 * YouTube URL → embed URL. See @/lib/youtube.
 */

const STEPS = [
  {
    icon: Upload,
    label: "Upload",
    title: "Drag & drop your documents",
    body:
      "PDFs, Word docs, and images (JPG/PNG) up to 250 MB. Page count is auto-detected — for scanned PDFs we run a colour-page scan so you get accurate B&W vs colour pricing without guessing.",
  },
  {
    icon: Sliders,
    label: "Configure",
    title: "Pick paper, sides, binding, finishing",
    body:
      "Copies, A4/A3/A5, single- or double-sided, B&W or colour (auto or custom per-page), stapling, lamination, spiral / tape / hardcover binding with dual-colour covers, and even gold-foil engraving on hardcover. Live price updates as you tweak.",
  },
  {
    icon: CreditCard,
    label: "Pay",
    title: "Pay with M-Pesa STK Push",
    body:
      "Enter your M-Pesa number, tap Pay, approve the STK prompt on your phone. Office Pack members can also settle directly from their prepaid wallet — no PIN required.",
  },
  {
    icon: PackageCheck,
    label: "Track & receive",
    title: "We print. You track. You collect.",
    body:
      "Get instant WhatsApp + email confirmation, live status updates as your job moves through Printing → Ready → Dispatched. Pick up in-shop free, or opt for Nairobi delivery or countrywide courier.",
  },
];

const FAQ = [
  {
    q: "What file types can I upload?",
    a: "PDF (best — page count is exact), Microsoft Word (.doc, .docx — page count is estimated and editable), and images (JPG, PNG). Maximum file size is 250 MB per document; you can upload multiple documents in one order.",
  },
  {
    q: "How is my price calculated?",
    a: "Base rate = per-page rate × pages × copies. Binding adds a tier-based fee (spiral / tape / hardcover). Stapling is per booklet. Lamination is per page. When you enable Batch copies, all N copies collapse into one physical volume so finishing is charged once — big savings for record books. A small convenience fee covers the M-Pesa transaction cost.",
  },
  {
    q: "How long does an order take?",
    a: "Normal: within 2 hours of payment. Urgent (+50 %): within 60 minutes. Priority (+100 %): within 30 minutes. These are best-effort estimates during business hours; if your job is unusually large we'll WhatsApp you with an updated ETA.",
  },
  {
    q: "How do I pay?",
    a: "M-Pesa STK Push — you enter your phone, we push a payment prompt to your phone, you approve with your M-Pesa PIN. Office Pack (B2B) members can also pay directly from their prepaid wallet at checkout with no STK required.",
  },
  {
    q: "What if the STK PIN prompt doesn't come through?",
    a: "Tap the \"Already paid? Confirm payment\" button on the payment screen. We'll query Safaricom directly and, if paid, take you straight to tracking. This also works if the STK popup timed out but the transaction succeeded on Safaricom's side.",
  },
  {
    q: "Can I get my order delivered?",
    a: "Yes. Pickup in-shop is free. Nairobi delivery is priced at Dispatch time and can be paid by M-Pesa or in cash-on-delivery. Countrywide courier (G4S / Fargo / Wells Fargo, etc.) is 1-3 business days depending on your county.",
  },
  {
    q: "What happens to my documents after printing?",
    a: "Uploaded files are automatically purged from our servers 7 days after your order is marked completed or cancelled. Only staff assigned to your specific job can see and download the file. All admin actions are logged in an immutable audit trail.",
  },
  {
    q: "Is my document confidential?",
    a: "Yes. Every staff member signs a confidentiality agreement. Non-privileged staff see phone numbers masked. We never scan, index, or use your documents for any purpose other than fulfilling your order. See our Terms & Conditions for full details.",
  },
  {
    q: "Can I re-order a previous job?",
    a: "Yes — head to your Dashboard, find the order, and tap Reorder. We'll prefill your customer info, delivery details, tier, and per-file specifications (paper, colour, binding, etc.). Drop in the same file(s) and continue to payment.",
  },
  {
    q: "What if the print quality has a defect?",
    a: "Notify us within 48 hours of delivery with a photo of the issue (wrong paper, colour bleed, binding damage, missing pages). We'll reprint at no cost or refund at our discretion. Contact us via the phone/email on the Terms & Conditions page.",
  },
];

function FaqRow({ q, a, idx, open, onToggle }) {
  return (
    <div className="border-b border-[#E5E5E5] last:border-b-0" data-testid={`faq-row-${idx}`}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-4 py-5 text-left hover:text-[#059669] transition-colors"
      >
        <span className="font-display font-bold text-lg text-[#0A0A0A]">{q}</span>
        <ChevronDown
          className={`w-5 h-5 flex-shrink-0 transition-transform ${open ? "rotate-180 text-[#059669]" : "text-[#525252]"}`}
          strokeWidth={2.5}
        />
      </button>
      {open && (
        <div className="pb-5 pr-9 text-[15px] leading-relaxed text-[#525252]" data-testid={`faq-body-${idx}`}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function HowItWorks() {
  const { info } = useBusinessInfo();
  const [openFaq, setOpenFaq] = useState(0);
  const embedUrl = useMemo(() => toYouTubeEmbed(info?.tutorial_video_url), [info?.tutorial_video_url]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-16" data-testid="how-it-works-page">
      {/* Hero */}
      <div className="mb-14">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Getting started</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-1 mb-4">
          How {info?.name || "CTP Kenya"} works
        </h1>
        <p className="text-lg text-[#525252] max-w-2xl">
          Four short steps from your laptop to a bound, packaged, and stapled stack of paper in your hands. No accounts required, no back-and-forth phone calls, no guessing the price.
        </p>
      </div>

      {/* Step-by-step */}
      <div className="grid md:grid-cols-2 gap-6 mb-16">
        {STEPS.map(({ icon: Icon, label, title, body }, idx) => (
          <div
            key={label}
            data-testid={`step-${idx + 1}`}
            className="border-2 border-[#E5E5E5] p-6 hover:border-[#0A0A0A] transition-colors group"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 bg-[#0A0A0A] text-white flex items-center justify-center group-hover:bg-[#059669] transition-colors">
                <Icon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">Step 0{idx + 1}</div>
                <div className="mono text-xs text-[#059669] font-bold">{label.toUpperCase()}</div>
              </div>
            </div>
            <h3 className="font-display font-black text-2xl mb-2">{title}</h3>
            <p className="text-[15px] text-[#525252] leading-relaxed">{body}</p>
          </div>
        ))}
      </div>

      {/* Video tutorial */}
      <div className="mb-16" data-testid="tutorial-video-section">
        <div className="flex items-center gap-3 mb-5">
          <PlayCircle className="w-6 h-6 text-[#059669]" strokeWidth={2.5} />
          <h2 className="font-display font-black text-3xl">Watch the 2-minute tutorial</h2>
        </div>
        {embedUrl ? (
          <div className="relative w-full border-2 border-[#0A0A0A]" style={{ paddingBottom: "56.25%" }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src={embedUrl}
              title="Tutorial video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="tutorial-video-iframe"
            />
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-10 text-center"
            data-testid="tutorial-video-placeholder"
          >
            <PlayCircle className="w-12 h-12 text-[#B0B0B0] mx-auto mb-3" strokeWidth={1.5} />
            <div className="text-sm text-[#525252] font-bold uppercase tracking-[0.15em]">Video coming soon</div>
            <div className="text-xs text-[#B0B0B0] mt-1">
              Super admins can paste the YouTube URL under Admin → Integrations → Tutorial video.
            </div>
          </div>
        )}
      </div>

      {/* FAQ */}
      <div className="mb-16">
        <div className="flex items-center gap-3 mb-5">
          <MessageCircleQuestion className="w-6 h-6 text-[#059669]" strokeWidth={2.5} />
          <h2 className="font-display font-black text-3xl">Frequently asked questions</h2>
        </div>
        <div className="border-2 border-[#E5E5E5]" data-testid="faq-list">
          {FAQ.map((f, i) => (
            <FaqRow
              key={f.q}
              q={f.q}
              a={f.a}
              idx={i}
              open={openFaq === i}
              onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
            />
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-[#0A0A0A] text-white p-8 sm:p-10 text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60 mb-2">§ Ready?</div>
        <h2 className="font-display font-black text-3xl sm:text-4xl mb-6">
          Upload your first document
        </h2>
        <Link
          to="/order"
          data-testid="how-it-works-cta"
          className="inline-flex items-center gap-2 bg-[#059669] px-8 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:bg-white hover:text-[#0A0A0A] transition-colors"
        >
          Start a new order <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

